import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { httpClient } from "@coworker/http-client";
import { Item } from "../../types/item";
import { isChineseEnvironment } from "@coworker/reusable/helpers/devices";
import { formatProductIdWithDots } from "@coworker/reusable/formatters/product.formatter";
import { INTERVAL } from "@coworker/app/src/constants/clientTime";
import { fetchItemInfo } from "./useItemInfo";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import { getItemQueryKey } from "@coworker/apprestructured/src/shared/hooks/item/queryKeys";

const NUMBER_OF_ITEMS_PER_CALL = 40; // Selling Range (SR) has a limit of 50 items per call, but we use 40 to avoid hitting the limit.
const undefinedWord = "undefined";

export async function fetchItemsInfo(itemNos: string): Promise<Item[]> {
  if (!itemNos) {
    return [];
  }

  const isChina = isChineseEnvironment();
  const itemNoList = itemNos
    .split(",")
    .map((no) => no.trim())
    .filter((no) => no && no !== undefinedWord);

  if (itemNos.includes(undefinedWord)) {
    reportMessageToSentry(
      `Item numbers contain undefined`,
      { itemNos, itemNoList },
      "info"
    );
  }

  const chunkSize = NUMBER_OF_ITEMS_PER_CALL;
  const chunks = Array.from(
    { length: Math.ceil(itemNoList.length / chunkSize) },
    (_, index) => itemNoList.slice(index * chunkSize, (index + 1) * chunkSize)
  );

  const results: Item[] = [];

  const chunkPromises = chunks
    .filter((c) => c.length > 0)
    .map(async (chunk) => {
      const endpoint = isChina ? "items-info-basic" : "items-info";
      const result = await httpClient.tasks.get<Item[]>(
        `/items/${endpoint}?itemNos=${chunk.join(",")}`
      );
      return (
        result?.map((item: Item) => {
          return {
            ...item,
            noFormatted: formatProductIdWithDots(item?.no),
          } as Item;
        }) ?? []
      );
    });

  const chunkResults = await Promise.all(chunkPromises);
  chunkResults.forEach((chunk) => results.push(...chunk));

  return results;
}

export const useItemsInfoQueries = (itemNos: string[]) => {
  const uniqueItemNos = [...new Set(itemNos)];
  return useQueries({
    queries:
      uniqueItemNos?.map((itemNo) => {
        return {
          queryKey: getItemQueryKey(itemNo),
          queryFn: () => fetchItemInfo(itemNo),
          refetchOnWindowFocus: false,
          staleTime: INTERVAL.DAY,
        };
      }) ?? [],
  });
};

export function useItemsInfoBatch(itemNos?: string[]) {
  const itemNumbers = useMemo(
    () => (Array.isArray(itemNos) ? itemNos : []),
    [itemNos]
  );

  const uniqueSortedKey = useMemo(
    () => [...new Set(itemNumbers)].sort().join(","),
    [itemNumbers]
  );

  return useQuery({
    queryKey: ["items", "info", uniqueSortedKey],
    queryFn: () => fetchItemsInfo(uniqueSortedKey),
    enabled: uniqueSortedKey.length > 0,
    staleTime: INTERVAL.WEEK,
    gcTime: INTERVAL.WEEK,
    refetchOnMount: false, // prevents refetching when returning to page
  });
}
